import React, { useMemo } from 'react';
import { useSortBy, useTable } from 'react-table';
import { InfoOutlineIcon, TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Image,
  Skeleton,
  Table,
  TableContainer,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Tooltip,
} from '@chakra-ui/react';
import { t } from '#i18n';
import { durationToString, secondsToMs } from '#utils';
import { NoDataFound } from './NoDataFound';

const SKELETON_ROWS = 4;

const renderSortIcon = (column) => {
  if (!column.isSorted) {
    return <Box width={4} />;
  }

  return column.isSortedDesc ? (
    <TriangleDownIcon role="img" aria-label="sorted descending" marginLeft={1} />
  ) : (
    <TriangleUpIcon role="img" aria-label="sorted ascending" marginLeft={1} />
  );
};

const renderHeaders = (headerGroup) => {
  const groupProps = headerGroup.getHeaderGroupProps();
  return (
    <Tr {...groupProps} key={groupProps.key}>
      {headerGroup.headers.map((column) => (
        <Th
          paddingY={4}
          paddingX={4}
          minWidth={column.id === 'user' ? 52 : undefined}
          backgroundColor="primary.400"
          borderColor="primary.300"
          {...column.getHeaderProps(column.getSortByToggleProps())}
          color="primary.50"
          isNumeric={column.isNumeric}
          key={column.id}
        >
          <Flex alignItems="center" justifyContent="flex-start">
            {column.help && (
              <Tooltip label={column.help}>
                <InfoOutlineIcon marginLeft={-4} marginRight={1} opacity={0.5} />
              </Tooltip>
            )}
            <Text whiteSpace="pre-line">
              {column.render('Header')}
            </Text>
            {renderSortIcon(column)}
          </Flex>
        </Th>
      ))}
    </Tr>
  );
};

const renderUserCell = ({ row }) => (
  <Flex alignItems="center">
    <Image
      boxSize={6}
      src={row.original.avatarUrl}
      alt={row.original.user}
      borderRadius={row.original.userType === 'USER' ? 'full' : 'md'}
      marginRight={2}
    />
    <Text>{row.original.user}</Text>
  </Flex>
);

const renderDurationCell = ({ value }) => durationToString(secondsToMs(value));

const renderIntegerCell = ({ value }) => value?.toLocaleString();

const renderFloatCell = (digits) => ({ value }) => value
  ?.toLocaleString(undefined, { maximumFractionDigits: digits });

const lineChanges = (type) => ({ value }) => {
  const [symbol, color] = type === 'additions' ? ['+', 'green.500'] : ['-', 'red'];
  return <Text color={color}>{`${symbol}${renderIntegerCell({ value })}`}</Text>;
};

const columns = [
  {
    Header: t('common.user.singular'),
    accessor: 'user',
    Cell: renderUserCell,
  },
  {
    Header: t('stats.pullRequestReviews.totalReviews'),
    help: t('stats.help.totalReviews'),
    accessor: 'totalReviews',
    Cell: renderIntegerCell,
  },
  {
    Header: t('stats.pullRequestReviews.medianTimeToReview'),
    help: t('stats.help.medianTimeToReview'),
    accessor: 'medianTimeToReview',
    Cell: renderDurationCell,
  },
  {
    Header: t('stats.pullRequestReviews.totalComments'),
    help: t('stats.help.totalComments'),
    accessor: 'totalComments',
    Cell: renderIntegerCell,
  },
  {
    Header: t('stats.pullRequestReviews.averageComments'),
    help: t('stats.help.averageComments'),
    accessor: 'averageComments',
    Cell: renderFloatCell(2),
  },
  {
    Header: t('stats.pullRequestReviews.totalAdditions'),
    help: t('stats.help.totalAdditions'),
    accessor: 'totalAdditions',
    Cell: lineChanges('additions'),
  },
  {
    Header: t('stats.pullRequestReviews.totalDeletions'),
    help: t('stats.help.totalDeletions'),
    accessor: 'totalDeletions',
    Cell: lineChanges('deletions'),
  },
];

const getColumnsWithLoadingState = (isLoading) => {
  if (isLoading) {
    return columns.map((column) => ({
      ...column,
      Cell: <Skeleton height="20px" data-testid="skeleton-cell" />,
    }));
  }

  return columns;
};

const getDataWithLoadingState = (data, isLoading) => {
  if (isLoading) {
    return Array(SKELETON_ROWS).fill({});
  }

  return data || [];
};

export const PerformanceTable = ({ data, isLoading }) => {
  const columnsWithLoadingState = useMemo(() => getColumnsWithLoadingState(isLoading), [isLoading]);
  const dataWithLoadingState = useMemo(() => (
    getDataWithLoadingState(data, isLoading)
  ), [data, isLoading]);

  const {
    headerGroups,
    rows,
    getTableProps,
    getTableBodyProps,
    prepareRow,
  } = useTable({ columns: columnsWithLoadingState, data: dataWithLoadingState }, useSortBy);

  const renderRow = (row, rowIdx) => {
    prepareRow(row);
    const isLastRow = rowIdx === rows.length - 1;
    const rowProps = row.getRowProps();

    return (
      <Tr {...rowProps} key={rowProps.key}>
        {row.cells.map((cell) => (
          <Td
            {...cell.getCellProps()}
            borderColor={isLastRow ? 'transparent' : 'primary.300'}
            isNumeric={cell.column.isNumeric}
            key={`${cell.row.id}:${cell.column.id}`}
          >
            {cell.render('Cell')}
          </Td>
        ))}
      </Tr>
    );
  };

  return (
    <TableContainer
      borderRadius="md"
      borderWidth="1px"
      borderColor="primary.300"
      width="full"
    >
      {dataWithLoadingState.length === 0 && !isLoading && (
        <Flex
          alignItems="center"
          justifyContent="center"
          width="full"
          height="255px"
        >
          <NoDataFound />
        </Flex>
      )}
      {dataWithLoadingState.length > 0 && (
        <Table {...getTableProps()}>
          <Thead>{headerGroups.map(renderHeaders)}</Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map((row, rowIdx) => renderRow(row, rowIdx))}
          </Tbody>
        </Table>
      )}
    </TableContainer>
  );
};
